// SPDX-License-Identifier: AGPL-3.0-or-later

import GuildCastDisplay from '@app/features/cast/state/GuildCastDisplay';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {useMemo} from 'react';

export interface MultiCharacterHead {
	id: string;
	displayName: string;
	avatarUrl: string | null;
	referenceImageUrl: string | null;
}

/**
 * Resolves the ordered list of character identities to render for a message attributed to more than
 * one character, or undefined when the message is not multi-character or none of its characters
 * resolve (in which case it falls back to the normal sender header, the same way a single unresolved
 * character does in useInCharacterOverride).
 *
 * This is the multi-character counterpart to useInCharacterOverride: that hook handles exactly one
 * character (substituting a single name + avatar); this one handles two or more, each rendered as
 * its own self-contained avatar + name pair. The single- and no-character paths are untouched.
 *
 * Characters are kept in castCharacterIds order and any that no longer resolve in the guild's cast
 * are dropped rather than asserting a stale identity. As with the single hook, the GuildCastDisplay
 * lookup is read during render (not gated behind useMemo deps) so an observer component re-renders
 * once the guild's cast finishes loading; useMemo only stabilises the array reference by content.
 */
export function useMultiCharacterHeads(
	message: Message,
	guildId: string | undefined,
): ReadonlyArray<MultiCharacterHead> | undefined {
	const resolvedGuildId = message.guildId ?? guildId;
	// Only a message sent with 2+ characters can ever be multi; skip the lookups otherwise.
	const isMultiCandidate = message.ic && message.castCharacterIds.length > 1;
	const heads: Array<MultiCharacterHead> = [];
	if (isMultiCandidate) {
		for (const id of message.castCharacterIds) {
			const identity = GuildCastDisplay.getChannelIdentity(resolvedGuildId, message.channelId, id);
			if (identity) {
				heads.push({
					id,
					displayName: identity.name,
					avatarUrl: identity.avatarUrl,
					referenceImageUrl: identity.referenceImageUrl,
				});
			}
		}
	}
	// Stable content key for the memo. JSON over the head objects, which are built just above with a
	// fixed key order, so equal content always yields an equal string — and no hand-rolled separator
	// that a name or URL could itself contain.
	const signature = JSON.stringify(heads);
	// Multi layout only when at least TWO characters still resolve. One resolvable (the rest removed
	// from the cast) falls through to the single head via useInCharacterOverride; zero falls through to
	// the sender — so a message never keeps multi-character spacing around fewer heads than it assumes.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	return useMemo(() => (heads.length >= 2 ? heads : undefined), [signature]);
}
